import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Get today's date string in Jordan timezone (UTC+3)
const getJordanToday = () => {
  const now    = new Date();
  const jordan = new Date(now.getTime() + (3 * 60 * 60 * 1000));
  const y = jordan.getUTCFullYear();
  const m = String(jordan.getUTCMonth() + 1).padStart(2, '0');
  const d = String(jordan.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parse a YYYY-MM-DD string safely without timezone shift
const parseDateUTC = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
};

export default function MonthlyOncall({ isMobile, onClose }) {
  const { profile } = useAuth();
  const isDirector  = profile?.role === 'director';

  const today   = getJordanToday();
  const todayD  = parseDateUTC(today);

  const [year, setYear]   = useState(todayD.getUTCFullYear());
  const [month, setMonth] = useState(todayD.getUTCMonth());
  const [entries, setEntries] = useState([]);
  const [fellows, setFellows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => { fetchEntries(); }, [year, month]);
  useEffect(() => {
    if (isDirector) {
      supabase.from('profiles').select('full_name').eq('role','fellow')
        .then(({ data }) => setFellows(data || []));
    }
  }, [isDirector]);

  const fetchEntries = async () => {
    setLoading(true);
    const start   = `${year}-${String(month+1).padStart(2,'0')}-01`;
    const lastDay = new Date(Date.UTC(year, month+1, 0)).getUTCDate();
    const end     = `${year}-${String(month+1).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
    const { data } = await supabase.from('monthly_oncall').select('*')
      .gte('oncall_date', start).lte('oncall_date', end).order('oncall_date');
    setEntries(data || []);
    setLoading(false);
  };

  const getDaysInMonth = () => {
    const days = [];
    const lastDay = new Date(Date.UTC(year, month+1, 0)).getUTCDate();
    for (let d = 1; d <= lastDay; d++) {
      const m = String(month+1).padStart(2,'0');
      const dd = String(d).padStart(2,'0');
      days.push(`${year}-${m}-${dd}`);
    }
    return days;
  };

  const startEditing = () => {
    const days = getDaysInMonth();
    const rows = days.map(dateStr => {
      const existing = entries.find(e => e.oncall_date === dateStr);
      return { date: dateStr, fellow_name: existing?.fellow_name || '', id: existing?.id || null };
    });
    setEditRows(rows);
    setEditing(true);
  };

  const saveAll = async () => {
    setSaving(true);
    for (const row of editRows) {
      if (!row.fellow_name.trim()) continue;
      if (row.id) {
        await supabase.from('monthly_oncall').update({ fellow_name: row.fellow_name, updated_at: new Date().toISOString() }).eq('id', row.id);
      } else {
        await supabase.from('monthly_oncall').upsert({ oncall_date: row.date, fellow_name: row.fellow_name }, { onConflict: 'oncall_date', ignoreDuplicates: false });
      }
    }
    await fetchEntries();
    setSaving(false); setEditing(false); setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const days = getDaysInMonth();

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:580, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>

        {/* Header */}
        <div style={{ background:'#1A3A5C', padding:'14px 20px', borderRadius:'14px 14px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:'#F5F0E8' }}>📅 Monthly On-Call Schedule</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#7EB8D4', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        {/* Month navigator */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderBottom:'1px solid #F0EDE8', background:'#FAFAF8' }}>
          <button onClick={prevMonth} style={ns.navBtn}>← Prev</button>
          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:600, color:'#1A3A5C' }}>{MONTHS[month]} {year}</span>
          <button onClick={nextMonth} style={ns.navBtn}>Next →</button>
        </div>

        {saved && <div style={{ background:'#F0FFF6', border:'1px solid #A8DCC0', color:'#1E5C40', padding:'8px 16px', fontSize:13, fontWeight:600 }}>✓ Schedule saved!</div>}

        {/* Content */}
        <div style={{ flex:1, overflowY:'auto', padding:'0 20px 16px' }}>
          {loading ? (
            <div style={{ padding:'40px 0', textAlign:'center', color:'#888' }}>Loading…</div>
          ) : editing ? (
            <div style={{ paddingTop:14 }}>
              <p style={{ fontSize:12, color:'#888', marginBottom:14 }}>Assign a fellow to each day. Leave blank to skip.</p>
              {editRows.map((row, i) => {
                const d         = parseDateUTC(row.date);
                const dayName   = DAYS[d.getUTCDay()];
                const isWeekend = d.getUTCDay() === 5 || d.getUTCDay() === 6;
                const dayNum    = d.getUTCDate();
                return (
                  <div key={row.date} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                    <div style={{ width:80, flexShrink:0 }}>
                      <span style={{ fontSize:13, fontWeight:700, color: isWeekend?'#B8892A':'#1A3A5C' }}>{dayName}</span>
                      <span style={{ fontSize:11, color:'#888', marginLeft:6 }}>{dayNum}</span>
                    </div>
                    <select value={row.fellow_name}
                      onChange={e => setEditRows(rows => rows.map((r,j) => j===i ? {...r, fellow_name: e.target.value} : r))}
                      style={{ ...es.input, flex:1, marginBottom:0 }}>
                      <option value=''>— Not assigned —</option>
                      {fellows.map(f => <option key={f.full_name} value={f.full_name}>{f.full_name}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ paddingTop:14 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid #E8E4E0' }}>
                    <th style={ns.th}>Date</th>
                    <th style={ns.th}>Day</th>
                    <th style={ns.th}>Fellow On-Call</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map(dateStr => {
                    const d         = parseDateUTC(dateStr);
                    const dayName   = DAYS[d.getUTCDay()];
                    const dayNum    = d.getUTCDate();
                    const isToday   = dateStr === today;
                    const isWeekend = d.getUTCDay() === 5 || d.getUTCDay() === 6;
                    const entry     = entries.find(e => e.oncall_date === dateStr);
                    return (
                      <tr key={dateStr} style={{ background: isToday?'rgba(184,137,42,0.06)':'transparent', borderBottom:'1px solid #F5F5F5' }}>
                        <td style={{ ...ns.td, fontWeight: isToday?700:400, color: isToday?'#B8892A':'#1A2535' }}>
                          {dayNum} {MONTHS[d.getUTCMonth()].slice(0,3)}
                          {isToday && <span style={{ marginLeft:6, fontSize:9, background:'#B8892A', color:'#fff', padding:'1px 6px', borderRadius:20, fontWeight:700 }}>Today</span>}
                        </td>
                        <td style={{ ...ns.td, color: isWeekend?'#B8892A':'#555', fontWeight: isWeekend?600:400 }}>{dayName}</td>
                        <td style={{ ...ns.td, color: entry?.fellow_name?'#1A3A5C':'#bbb', fontWeight: entry?.fellow_name?500:400 }}>
                          {entry?.fellow_name || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid #F0EDE8', display:'flex', gap:10 }}>
          {isDirector && !editing && (
            <button onClick={startEditing} style={{ flex:1, padding:'10px', background:'#1A3A5C', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" }}>
              ✎ Edit Schedule
            </button>
          )}
          {editing && (
            <>
              <button onClick={() => setEditing(false)} style={{ padding:'10px 16px', background:'#EEE', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontFamily:"'DM Sans'" }}>Cancel</button>
              <button onClick={saveAll} disabled={saving} style={{ flex:1, padding:'10px', background:'#1E5C40', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'", opacity:saving?0.7:1 }}>
                {saving?'Saving…':'💾 Save Schedule'}
              </button>
            </>
          )}
          {!editing && (
            <button onClick={onClose} style={{ flex: isDirector?'unset':1, padding:'10px 20px', background:'#EEE', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontFamily:"'DM Sans'" }}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

const ns = {
  navBtn: { padding:'6px 14px', background:'#F0F4F8', border:'1px solid rgba(26,58,92,0.12)', borderRadius:7, fontSize:13, color:'#1A3A5C', cursor:'pointer', fontFamily:"'DM Sans'", fontWeight:500 },
  th: { padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#888', textTransform:'uppercase', letterSpacing:'0.5px' },
  td: { padding:'8px 12px', fontSize:13 },
};

const es = {
  input: { width:'100%', padding:'10px 12px', border:'1.5px solid #E0DDD8', borderRadius:8, fontSize:13, color:'#1A1A1F', background:'#FAFAF8', outline:'none', fontFamily:"'DM Sans'", boxSizing:'border-box' },
};
